import { disableScroll, enableScroll } from 'application/utils/helpers'

/**
 * Интерфейс состояния шапки сайта
 */
export interface HeaderState {
  /** (HTMLElement | null) Корневой элемент шапки */
  headerEl: HTMLElement | null
  /** (HTMLElement | null) Элемент кнопки бургера */
  burger: HTMLElement | null
  /** (ReturnType<typeof initNavigation> | null) Экземпляр навигации */
  navigation: ReturnType<typeof initNavigation> | null
  /** Скрытая шапка */
  isHidden: boolean
  /** Открытое мобильное меню */
  mobileMenuOpen: boolean
  /** (number) Предыдущая Y позиция скролла */
  prevYPosition: number
  canUpdate: boolean
}

/**
 * Обновляет состояние скрытия шапки
 *
 * @param {HeaderState} state - Состояние шапки
 */
function updateHidden(state: HeaderState) {
  if (!state.headerEl) return
  state.headerEl.classList.toggle('header_hidden', state.isHidden)
}

/**
 * Обновляет состояние мобильного меню
 *
 * @param {HeaderState} state - Состояние шапки
 */
function updateMobileMenu(state: HeaderState) {
  if (!state.headerEl) return

  state.burger?.classList.toggle('opened', state.mobileMenuOpen)

  state.navigation?.setIsOpen(state.mobileMenuOpen)

  if (state.mobileMenuOpen) {
    disableScroll()
  } else {
    enableScroll()
  }

  state.headerEl.dispatchEvent(
    new CustomEvent('header:menu', { detail: { isOpen: state.mobileMenuOpen } })
  )
}

/**
 * Инициализирует прилипающую шапку с использованием IntersectionObserver
 *
 * @param {HeaderState} state - Состояние шапки
 * @param {HTMLElement} selector - Элемент для наблюдения
 */
function initStickyHeader(state: HeaderState) {
  let lastScroll = 0
  const headerHeight = state.headerEl?.offsetHeight || 0

  const updateHeader = () => {
    if (document.body.hasAttribute('data-noscroll-target-scroll-disabled'))
      return

    const currentScroll = window.scrollY

    const direction = currentScroll >= lastScroll ? 'down' : 'up'

    state.headerEl?.classList.toggle('header_bg-black', currentScroll > 0)

    if (direction === 'up' && state.isHidden) {
      state.isHidden = false
      updateHidden(state)
    } else if (
      direction === 'down' &&
      currentScroll >= headerHeight &&
      !state.isHidden
    ) {
      state.isHidden = true
      updateHidden(state)
    }

    lastScroll = currentScroll
  }

  const requestTick = () => {
    if (!state.canUpdate) return
    requestAnimationFrame(updateHeader)
  }

  window.addEventListener('scroll', requestTick, { passive: true })
  updateHeader()
}

/**
 * Инициализирует шапку сайта со всеми её компонентами
 */
export function initHeader() {
  const headerEl = document.querySelector<HTMLElement>('[data-header]')
  if (!headerEl) return

  const state: HeaderState = {
    headerEl,
    burger: null,
    navigation: null,
    isHidden: false,
    mobileMenuOpen: false,
    prevYPosition: 0,
    canUpdate: true
  }

  initStickyHeader(state)

  state.navigation = initNavigation(headerEl)

  // nav → header sync
  headerEl.addEventListener('nav:open', () => {
    state.canUpdate = false
  })

  headerEl.addEventListener('nav:close', () => {
    setTimeout(() => {
      state.canUpdate = true
    }, 400)
  })

  // burger
  state.burger = headerEl.querySelector('[data-burger]') as HTMLElement | null
  if (state.burger) {
    const allyTextEl = state.burger.querySelector(
      '[data-burger="ally"]'
    ) as HTMLElement | null
    state.burger.addEventListener('click', () => {
      state.mobileMenuOpen = !state.mobileMenuOpen
      updateMobileMenu(state)

      if (allyTextEl) {
        allyTextEl.textContent = state.mobileMenuOpen
          ? 'Закрыть меню'
          : 'Открыть меню'
      }
    })
  }

  // auth
  const authButton = headerEl.querySelector('[data-auth-button]')
  authButton?.addEventListener('click', () => {
    document.dispatchEvent(new CustomEvent('auth:open'))
  })

  // search toggle
  const search = headerEl.querySelector('[data-search-toggle]')
  search?.addEventListener('click', () => {
    document.dispatchEvent(new CustomEvent('search:toggle'))
  })

  // Выбор города — обычные ссылки на /:city_slug (см. _header.html.erb),
  // JS-обработчик не нужен: прежние слушали атрибуты, которых в разметке нет,
  // и вели на поддомен вида 6.claustrophobia.com.

  // language select
  const langSelect = headerEl.querySelector('[data-language-select]')
  langSelect?.addEventListener('dropdown:value-updated', (e: Event) => {
    const detail: { id: string; value: string } = (e as CustomEvent).detail
    document.cookie = `language-id=${detail.value}; path=/; max-age=${86400 * 7}`
    document.dispatchEvent(
      new CustomEvent('language:changed', { detail: (e as CustomEvent).detail })
    )
  })
}

/**
 * Интерфейс состояния навигации
 */
export interface NavigationState {
  /** (HTMLElement | null) Корневой элемент навигации */
  navigationEl: HTMLElement | null
  /** (HTMLElement[]) Элементы пунктов навигации */
  navigationItems: HTMLElement[]
  /** Открыта ли навигация */
  isOpen: boolean
  /** Прилипшая навигация */
  isSticky: boolean
  /** Мобильный режим */
  isMobile: boolean
  /** (MediaQueryList | null) Media query для мобильных */
  mobileMediaQuery: MediaQueryList | null
  /** (number) Индекс активного пункта в мобильной версии */
  mobileActiveIndex: number
  /** (number | null) Таймер отложенного закрытия */
  closeTimeoutId: number | null
}

/**
 * Обновляет состояние прилипания навигации
 *
 * @param {NavigationState} state - Состояние навигации
 */
function updateSticky(state: NavigationState) {
  if (!state.navigationEl) return

  state.navigationEl.classList.toggle('nav_sticky', state.isSticky)
}

/**
 * Обновляет активный пункт меню в мобильной версии
 *
 * @param {NavigationState} state - Состояние навигации
 */
function updateMobileIndex(state: NavigationState) {
  state.isOpen = state.mobileActiveIndex >= 0

  if (!state.navigationEl) return

  if (state.isOpen) {
    state.navigationEl.classList.add('nav_opened')
    state.navigationEl.dispatchEvent(
      new CustomEvent('nav:open', { bubbles: true })
    )
  } else {
    state.navigationEl.classList.remove('nav_opened')
    state.navigationEl.dispatchEvent(
      new CustomEvent('nav:close', { bubbles: true })
    )
  }

  state.navigationItems.forEach((item, idx) => {
    item.classList.toggle('nav__button_active', idx === state.mobileActiveIndex)
  })
}

/**
 * Обновляет состояние при изменении мобильного режима
 *
 * @param {NavigationState} state - Состояние навигации
 */
function updateIsMobile(state: NavigationState) {
  clearCloseTimer(state)
  state.mobileActiveIndex = -1
  updateMobileIndex(state)
}

/**
 * Сбрасывает таймер закрытия меню
 *
 * @param {NavigationState} state - Состояние навигации
 */
function clearCloseTimer(state: NavigationState) {
  if (state.closeTimeoutId === null) return
  window.clearTimeout(state.closeTimeoutId)
  state.closeTimeoutId = null
}

/**
 * Запускает таймер закрытия меню
 *
 * @param {NavigationState} state - Состояние навигации
 */
function scheduleClose(state: NavigationState) {
  clearCloseTimer(state)
  state.closeTimeoutId = window.setTimeout(() => {
    state.mobileActiveIndex = -1
    updateMobileIndex(state)
    state.closeTimeoutId = null
  }, 200)
}

/**
 * Создаёт обработчики событий для контейнера навигации
 *
 * @param {NavigationState} state - Состояние навигации
 * @return {any} Объект с обработчиками
 */
function createNavEvents(state: NavigationState) {
  return {
    mouseenter: () => {
      if (!state.isMobile) {
        clearCloseTimer(state)
      }
    },

    mouseleave: () => {
      if (!state.isMobile) {
        scheduleClose(state)
      }
    },

    focusout: ({ relatedTarget }: FocusEvent) => {
      const target = relatedTarget as HTMLElement | null
      if (!target) return

      if (
        target.hasAttribute('data-burger') ||
        target.closest('[data-header-nav]')
      ) {
        return
      }

      clearCloseTimer(state)
      state.mobileActiveIndex = -1
      updateMobileIndex(state)
    }
  }
}

/**
 * Создаёт обработчики событий для пунктов навигации
 *
 * @param {NavigationState} state - Состояние навигации
 * @return {any} Объект с обработчиками
 */
function createItemEvents(state: NavigationState) {
  return {
    mouseenter: (e: MouseEvent) => {
      if (state.isMobile) return
      clearCloseTimer(state)
      const el = e.target as HTMLElement
      const idx = Number(el.dataset.menuId)
      if (!Number.isNaN(idx)) {
        state.mobileActiveIndex = idx
        updateMobileIndex(state)
      }
    },

    mouseleave: (e: MouseEvent) => {
      if (state.isMobile) return
      const el = e.target as HTMLElement
      const related = e.relatedTarget as HTMLElement | null
      const item = el.closest('.nav__item')
      if (item && related && item.contains(related)) return
      scheduleClose(state)
    },

    focusin: (e: FocusEvent) => {
      const el = e.target as HTMLElement
      const btn = el.closest('.nav__button') as HTMLElement | null
      if (!btn) return

      clearCloseTimer(state)

      const idx = Number(btn.dataset.menuId)
      if (!Number.isNaN(idx)) {
        state.mobileActiveIndex = idx
        updateMobileIndex(state)
      }
    },

    click: (e: MouseEvent) => {
      const el = e.target as HTMLElement
      const idx = Number(el.dataset.menuId)
      if (!Number.isNaN(idx)) {
        clearCloseTimer(state)
        state.mobileActiveIndex = idx
        updateMobileIndex(state)
      }
    }
  }
}

/**
 * Инициализирует навигацию в шапке
 *
 * @param {HTMLElement} headerEl - Корневой элемент шапки
 * @return {any} Объект с публичными методами или null
 */
export default function initNavigation(headerEl: HTMLElement) {
  const state: NavigationState = {
    navigationEl: null,
    navigationItems: [],
    isOpen: false,
    isSticky: false,
    isMobile: false,
    mobileMediaQuery: null,
    mobileActiveIndex: -1,
    closeTimeoutId: null
  }

  state.navigationEl = headerEl.querySelector<HTMLElement>('[data-header-nav]')
  if (!state.navigationEl) return null

  // mobile match
  state.mobileMediaQuery = window.matchMedia('(max-width: 1439px)')
  state.isMobile = state.mobileMediaQuery.matches

  state.mobileMediaQuery.addEventListener('change', ({ matches }) => {
    state.isMobile = matches
    updateIsMobile(state)
  })

  // nav events
  const navEvents = createNavEvents(state)
  for (const [event, handler] of Object.entries(navEvents)) {
    state.navigationEl.addEventListener(event, handler as EventListener)
  }

  // items
  state.navigationItems = Array.from(
    state.navigationEl.querySelectorAll<HTMLElement>('[data-menu-id]')
  )

  const itemEvents = createItemEvents(state)
  for (const [event, handler] of Object.entries(itemEvents)) {
    state.navigationItems.forEach((item) =>
      item.addEventListener(event, handler as EventListener)
    )
  }

  // backdrop
  const backdrop = state.navigationEl.querySelector(
    '[data-header-nav-backdrop]'
  ) as HTMLElement | null
  if (backdrop) {
    backdrop.addEventListener('mouseenter', () => {
      if (!state.isMobile) {
        scheduleClose(state)
      }
    })
  }

  return {
    setIsSticky(v: boolean) {
      state.isSticky = v
      updateSticky(state)
    },

    setIsOpen(v: boolean) {
      if (v === state.isOpen) return

      if (!v) {
        state.mobileActiveIndex = -1
        updateMobileIndex(state)
        return
      }

      // open
      state.mobileActiveIndex =
        state.mobileActiveIndex >= 0 ? state.mobileActiveIndex : 0
      updateMobileIndex(state)

      if (state.isMobile) {
        setTimeout(() => {
          const first = state.navigationEl?.querySelector<HTMLElement>(
            '.nav__button a:first-child'
          )
          first?.focus()
        }, 100)
      }
    }
  }
}
